import { useState, useRef, useEffect, useCallback } from "react";
import { CheckCircle, XCircle, AlertTriangle, Wrench, Timer, Clock, ExternalLink, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import type { TechnicalChecks, TechCheckStatus } from "@/hooks/useTechnicalChecks";

interface ActionPlanProps {
  websiteUrl?: string;
  techStatus: TechCheckStatus;
  techData: TechnicalChecks | null;
}

// ── Check definitions ──
interface TechCheck {
  key: 'robotsTxt' | 'llmsTxt' | 'sitemap' | 'schemaMkp';
  title: string;
  passText: string;
  passDetail: string;
  failText: string;
  isWarning?: boolean;
  warningText?: string;
  timeEstimate: string;
  timeIcon: 'quick' | 'medium';
  linkText: string;
  linkUrl: string;
  tooltipText: string;
  insightText: string;
  steps: string[];
  footnote?: string;
}

const techChecks: TechCheck[] = [
  {
    key: 'robotsTxt',
    title: 'AI Crawler Access (robots.txt)',
    passText: 'AI crawlers are allowed on your site',
    passDetail: 'Your robots.txt allows AI crawlers (GPTBot, ClaudeBot, PerplexityBot) to access your site.',
    failText: 'AI crawlers are blocked. Fix your robots.txt',
    timeEstimate: '15 min',
    timeIcon: 'quick',
    linkText: 'Guide →',
    linkUrl: 'https://developers.google.com/search/docs/crawling-indexing/robots/intro',
    tooltipText: 'Many sites unknowingly block AI crawlers, preventing their content from appearing in AI responses. Research shows top 20 domains capture 67% of all AI citations (Yang, AAAI 2025). Being crawlable is the first step to being cited.',
    insightText: 'Without proper robots.txt rules, AI crawlers have no access to your site\'s content.',
    steps: [
      'Open your robots.txt file (usually at yoursite.com/robots.txt)',
      'Check for User-agent: GPTBot, ClaudeBot, or PerplexityBot with Disallow: /',
      'Remove any Disallow rules for AI crawlers you want to allow',
      'If using User-agent: * with Disallow: /, add specific Allow rules for AI bots',
    ],
  },
  {
    key: 'sitemap',
    title: 'XML Sitemap',
    passText: 'Sitemap found and accessible',
    passDetail: 'Your sitemap.xml was found and is accessible to search engines and AI crawlers.',
    failText: 'No sitemap found. AI crawlers may miss your pages',
    timeEstimate: '30 min',
    timeIcon: 'quick',
    linkText: 'Generator →',
    linkUrl: 'https://www.xml-sitemaps.com/',
    tooltipText: 'Content that isn\'t indexed can\'t be cited in AI-powered search results. AI crawlers rely on sitemaps to discover pages efficiently. An industry standard recommended by Google Search Central.',
    insightText: 'AI crawlers rely on sitemaps to discover your pages. Missing pages can\'t be cited.',
    steps: [
      'Generate a sitemap.xml using an online generator or your CMS',
      'Upload it to your site root (yoursite.com/sitemap.xml)',
      'Submit it to Google Search Console',
      'Ensure all important pages are included',
    ],
  },
  {
    key: 'llmsTxt',
    title: 'llms.txt file',
    passText: 'llms.txt file found',
    passDetail: 'Your llms.txt file provides structured information about your brand for AI models.',
    failText: 'No llms.txt found, optional but recommended',
    isWarning: true,
    warningText: 'No llms.txt found, optional but recommended',
    timeEstimate: '30 min',
    timeIcon: 'quick',
    linkText: 'Template →',
    linkUrl: 'https://llmstxt.org/',
    tooltipText: 'Adoption is still very early, and no major AI platform has confirmed using it for citations. Including it is low-effort and future-facing. Think of it as optional preparation, not a ranking factor.',
    insightText: 'Adding llms.txt gives AI models structured access to your brand information. Most competitors don\'t have one yet.',
    steps: [
      'Create llms.txt file in your website root directory',
      'Include company overview, products/services, and key differentiators',
      'Add structured data about your brand\'s unique value propositions',
    ],
  },
  {
    key: 'schemaMkp',
    title: 'Structured Data (Schema Markup)',
    passText: 'Schema markup detected on your site',
    passDetail: 'Schema markup was detected, helping AI models understand your site\'s content.',
    failText: 'No schema markup found. AI can\'t read your brand info',
    timeEstimate: '1.5 hours',
    timeIcon: 'medium',
    linkText: 'Practical Guide →',
    linkUrl: 'https://schema.org/Organization',
    tooltipText: 'Peer-reviewed research (KDD 2024) found that content optimization tactics can boost AI visibility by up to 40%. Structured data is the technical foundation that makes your brand machine-readable. Note: checks server-rendered JSON-LD only. Schema injected via JavaScript (e.g., GTM) may not be detected.',
    insightText: 'Structured data helps AI models understand your brand, products, and content at a glance.',
    steps: [
      'Organization Schema: Logo, social profiles, contact info',
      'FAQ Schema: 5-10 common questions about your service',
      'Validate with Google\'s Rich Results Test',
    ],
    footnote: 'Official reference: schema.org/Organization',
  },
];



// ── Helpers ──
type CheckState = 'pass' | 'fail' | 'warning';

function getCheckState(check: TechCheck, data: TechnicalChecks): CheckState {
  if (check.key === 'robotsTxt') {
    return data.robotsTxt.status === 'allowed' ? 'pass' : 
           data.robotsTxt.status === 'partially_blocked' ? 'warning' : 'fail';
  }
  if (check.key === 'llmsTxt') {
    return data.llmsTxt.status === 'found' ? 'pass' : 'warning';
  }
  const status = data[check.key].status;
  return status === 'found' ? 'pass' : 'fail';
}

function getStateIcon(state: CheckState) {
  switch (state) {
    case 'pass': return <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />;
    case 'warning': return <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0" />;
    case 'fail': return <XCircle className="w-5 h-5 text-destructive flex-shrink-0" />;
  }
}

function getStateText(check: TechCheck, state: CheckState, data: TechnicalChecks): string {
  if (state === 'pass') return check.passText;
  if (state === 'warning') {
    if (check.key === 'robotsTxt' && data.robotsTxt.status === 'partially_blocked') {
      return `Partially blocked: ${data.robotsTxt.blockedBots.join(', ')}`;
    }
    return check.warningText || check.failText;
  }
  return check.failText;
}

const getTimeIcon = (type: 'quick' | 'medium') => {
  switch (type) {
    case 'quick': return <Timer className="w-3 h-3" />;
    case 'medium': return <Clock className="w-3 h-3" />;
  }
};

const getTimeBadgeColor = (type: 'quick' | 'medium') => {
  switch (type) {
    case 'quick': return 'bg-success/10 text-success border-success/20';
    case 'medium': return 'bg-warning/10 text-warning border-warning/20';
  }
};

// ── Info Popover (hover on desktop, tap on mobile) ──
const InfoPopover = ({ text }: { text: string }) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    if (dismissTimeout.current) clearTimeout(dismissTimeout.current);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const handleOpen = () => {
    setOpen(true);
    clearTimers();
    if (!isMobile) {
      dismissTimeout.current = setTimeout(() => setOpen(false), 8000);
    }
  };

  const handlePointerEnter = () => {
    if (isMobile) return;
    clearTimers();
    hoverTimeout.current = setTimeout(handleOpen, 300);
  };

  const handlePointerLeave = () => {
    if (isMobile) return;
    clearTimers();
    hoverTimeout.current = setTimeout(() => setOpen(false), 200);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            if (isMobile) setOpen((v) => !v);
          }}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          <Info className="w-3.5 h-3.5 text-muted-foreground hover:text-primary cursor-help" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] max-w-[90vw] text-sm p-4"
        side="top"
        onPointerEnter={() => { if (!isMobile) { clearTimers(); } }}
        onPointerLeave={handlePointerLeave}
        onClick={(e) => e.stopPropagation()}
      >
        {text}
      </PopoverContent>
    </Popover>
  );
};

// ── Component ──
const ActionPlan = ({ websiteUrl, techStatus, techData }: ActionPlanProps) => {
  // Determine which accordion items should be auto-expanded (failed checks)
  const defaultOpenItems: string[] = [];
  if (techData) {
    techChecks.forEach((check) => {
      const state = getCheckState(check, techData);
      if (state === 'fail') {
        defaultOpenItems.push(check.key);
      }
    });
  }

  const showTechnicalSection = !!websiteUrl;

  return (
    <div id="technical-ai-checks" className="space-y-8">
      {/* Section 1: Technical AI-Readiness */}
      {showTechnicalSection && (
        <div className="space-y-3">
          <div className="space-y-1">
            <h3 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Technical AI Checks
            </h3>
            <p className="text-sm text-muted-foreground">
              Checks if AI crawlers can find and read your site
            </p>
          </div>

          {techStatus === 'loading' && (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          )}

          {techStatus === 'error' && (
            <div className="p-4 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground">
              Unable to run technical checks. Try refreshing the page.
            </div>
          )}

          {techStatus === 'done' && techData && (
            <Accordion
              type="multiple"
              defaultValue={defaultOpenItems}
              className="space-y-1"
            >
              {techChecks.map((check) => {
                const state = getCheckState(check, techData);
                const stateText = getStateText(check, state, techData);

                return (
                  <AccordionItem
                    key={check.key}
                    value={check.key}
                    className="border rounded-lg px-4 data-[state=open]:bg-muted/20"
                  >
                    <AccordionTrigger className="hover:no-underline py-3 gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        {getStateIcon(state)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{check.title}</span>
                            <InfoPopover text={check.tooltipText} />
                            {state === 'fail' && (
                              <Badge variant="outline" className="text-destructive border-destructive/30 text-[10px] px-1.5 py-0">
                                Action needed
                              </Badge>
                            )}
                          </div>
                          <p className={cn(
                            "text-xs mt-0.5",
                            state === 'pass' ? 'text-success' : state === 'warning' ? 'text-warning' : 'text-destructive'
                          )}>
                            {stateText}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {state === 'pass' ? (
                        <div className="pl-8 pb-1">
                          <p className="text-sm text-muted-foreground">{check.passDetail}</p>
                        </div>
                      ) : (
                        <div className="pl-8 pb-1 space-y-3">
                          <p className="text-xs text-muted-foreground italic">{check.insightText}</p>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className={cn(
                              "flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border",
                              getTimeBadgeColor(check.timeIcon)
                            )}>
                              {getTimeIcon(check.timeIcon)}
                              {check.timeEstimate}
                            </span>
                            <a
                              href={check.linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                            >
                              {check.linkText}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          <ul className="space-y-1.5">
                            {check.steps.map((step, i) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                <span className="text-primary mt-0.5">•</span>
                                {step}
                              </li>
                            ))}
                          </ul>
                          {check.footnote && (
                            <p className="text-[10px] text-muted-foreground/70 italic">{check.footnote}</p>
                          )}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionPlan;
